<template>
  <div class="rounded-md border-2 border-gray-300 bg-neutral-50 shadow p-3 m-2">
    <div class="flex flex-row items-center justify-between mb-2">
      <div class="text-sm tracking-widest text-gray-700 font-poppins uppercase">
        Daily Expense Activity — Last 14 Days
      </div>
      <div class="text-xs text-gray-500">
        <span v-if="loading">Loading…</span>
        <span v-else-if="errorMsg" class="text-rose-600">{{ errorMsg }}</span>
      </div>
    </div>
    <div class="h-72">
      <Bar :data="chartData" :options="chartOptions" />
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'vue-chartjs'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const props = defineProps({
  records: { type: Array, default: () => [] },
  transitions: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  errorMsg: { type: String, default: '' },
})

const ACTION_TO_SERIES = {
  submit: 'submitted',
  accounting_approve: 'approved',
  mark_submitter_reimbursed: 'reimbursed',
  mark_client_invoiced: 'invoiced',
}

function dayKey(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function buildBuckets() {
  const today = new Date()
  const out = []
  for (let i = 13; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i)
    out.push({ key: dayKey(d), date: d })
  }
  return out
}

const chartData = computed(() => {
  const buckets = buildBuckets()
  const indexOf = Object.fromEntries(buckets.map((b, i) => [b.key, i]))

  const counts = {
    created: new Array(14).fill(0),
    submitted: new Array(14).fill(0),
    approved: new Array(14).fill(0),
    reimbursed: new Array(14).fill(0),
    invoiced: new Array(14).fill(0),
  }

  for (const r of props.records) {
    if (!r.created_at) continue
    const key = dayKey(new Date(r.created_at))
    if (key in indexOf) counts.created[indexOf[key]]++
  }

  for (const t of props.transitions) {
    const series = ACTION_TO_SERIES[t.action]
    if (!series || !t.created_at) continue
    const key = dayKey(new Date(t.created_at))
    if (key in indexOf) counts[series][indexOf[key]]++
  }

  const labels = buckets.map((b) => `${b.date.getMonth() + 1}/${b.date.getDate()}`)

  return {
    labels,
    datasets: [
      { label: 'Created',    data: counts.created,    backgroundColor: '#3B82F6' },
      { label: 'Submitted',  data: counts.submitted,  backgroundColor: '#F59E0B' },
      { label: 'Approved',   data: counts.approved,   backgroundColor: '#10B981' },
      { label: 'Reimbursed', data: counts.reimbursed, backgroundColor: '#8B5CF6' },
      { label: 'Invoiced',   data: counts.invoiced,   backgroundColor: '#F43F5E' },
    ],
  }
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom' },
    tooltip: { mode: 'index', intersect: false },
  },
  scales: {
    x: { stacked: false, grid: { display: false } },
    y: {
      beginAtZero: true,
      ticks: { stepSize: 1, precision: 0 },
      grid: { color: 'rgba(0,0,0,0.05)' },
    },
  },
}
</script>
